import { db } from '../firebase';
import { collection, query, where, getDoc, getDocs, addDoc, deleteDoc, doc, updateDoc, increment, runTransaction } from 'firebase/firestore';
import { getUserProfileByEmail, createUserDocument, createOrUpdateUserDocument, sendWelcomeEmailIfNeeded } from '../users/userManagement';
import { createSession } from '../users/sessionManagement';
import { sendEventDetailEmail, sendWelcomeEmail, sendHostRSVPNotification, sendHostRSVPRemovalNotification } from '../emails/emailService';

// Add RSVP
export const addRSVP = async (eventId, userId, email) => {
  const eventRef = doc(db, 'events', eventId);

  try {
    await runTransaction(db, async (transaction) => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error("Event does not exist!");
      }

      const guestCount = userId.guestCount || 0;
      const name = userId.name || '';
      const displayAnonymous = userId.displayAnonymous || false;
      const status = userId.status || 'yes';
      const comment = userId.comment || '';
      // Total count includes the RSVP person plus their guests
      const newRsvpCount = (eventDoc.data().rsvpCount || 0) + 1 + guestCount;
      
      const newRsvpRef = await addDoc(collection(db, 'rsvps'), {
        eventId,
        userId: userId.userId || userId, // Handle both object and string cases
        email,
        guestCount,
        name,
        displayAnonymous,
        status,
        comment,
        createdAt: new Date()
      });

      transaction.update(eventRef, { rsvpCount: newRsvpCount });
    });

    return { success: true, message: 'RSVP added successfully' };
  } catch (error) {
    console.error("Error adding RSVP: ", error);
    return { success: false, error: error.message };
  }
};

// Remove RSVP (removedByHost: true when host removes an attendee's RSVP, so attendee email says "Host removed your RSVP")
export const removeRSVP = async (eventId, userEmail, removedByHost = false) => {
  const eventRef = doc(db, 'events', eventId);

  try {
    // Get RSVP data before deletion for notification
    const rsvpQuery = query(
      collection(db, 'rsvps'),
      where('eventId', '==', eventId),
      where('email', '==', userEmail)
    );
    const rsvpSnapshot = await getDocs(rsvpQuery);
    const rsvpData = rsvpSnapshot.empty ? null : rsvpSnapshot.docs[0].data();
    
    await runTransaction(db, async (transaction) => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error("Event does not exist!");
      }

      let guestCount = 0;
      if (!rsvpSnapshot.empty) {
        guestCount = rsvpSnapshot.docs[0].data().guestCount || 0;
      }

      // Subtract RSVP person plus their guests
      const newRsvpCount = Math.max((eventDoc.data().rsvpCount || 0) - (1 + guestCount), 0);

      if (!rsvpSnapshot.empty) {
        transaction.delete(rsvpSnapshot.docs[0].ref);
      }

      transaction.update(eventRef, { rsvpCount: newRsvpCount });
    });

    // Send notification to event host and confirmation to attendee (async, non-blocking)
    if (rsvpData) {
      const eventDoc = await getDoc(eventRef);
      const eventData = eventDoc.data();
      const eventPayload = { ...eventData, id: eventId, url: eventData.url };

      if (eventData.hostEmail && eventData.hostEmail !== userEmail) {
        getRSVPs(eventId).then(allRsvps => {
          sendHostRSVPRemovalNotification(
            eventData.hostEmail,
            eventData.hostName,
            eventPayload,
            {
              name: rsvpData.name || 'Guest',
              email: userEmail,
              status: rsvpData.status || 'yes',
              guestCount: rsvpData.guestCount || 0
            },
            allRsvps
          ).catch(error => {
            console.error('Error sending host removal notification:', error);
          });
        }).catch(error => {
          console.error('Error fetching RSVPs for removal notification:', error);
        });
      }

      // Send removal email to the attendee – subject varies by who removed (host vs self)
      sendEventDetailEmail(
        userEmail,
        rsvpData.name || 'Guest',
        eventPayload,
        false,
        false,
        'yes',
        true, // rsvpRemoved
        removedByHost
      ).catch(error => {
        console.error('Error sending RSVP removal confirmation email:', error);
      });
    }

    return { success: true, message: 'RSVP removed successfully' };
  } catch (error) {
    console.error("Error removing RSVP: ", error);
    return { success: false, error: error.message };
  }
};

// Check if user has RSVP'd and return RSVP data with document reference
export const checkRSVP = async (eventId, userEmail) => {
  const rsvpQuery = query(
    collection(db, 'rsvps'),
    where('eventId', '==', eventId),
    where('email', '==', userEmail)
  );
  const rsvpSnapshot = await getDocs(rsvpQuery);
  if (rsvpSnapshot.empty) {
    return null;
  }
  const rsvpDoc = rsvpSnapshot.docs[0];
  const rsvpData = rsvpDoc.data();
  return {
    ...rsvpData,
    status: rsvpData.status || 'yes', // Default to 'yes' for legacy RSVPs
    _doc: rsvpDoc // Include document reference for updates
  };
};

// Get all RSVPs for an event
export const getRSVPs = async (eventId) => {
  console.log('Fetching RSVPs for event:', eventId);
  const rsvpsRef = collection(db, 'rsvps');
  const q = query(rsvpsRef, where('eventId', '==', eventId));
  try {
    console.log('Executing query...');
    const querySnapshot = await getDocs(q);
    console.log(`Found ${querySnapshot.size} RSVPs for event ${eventId}`);
    const rsvps = await Promise.all(querySnapshot.docs.map(async doc => {
      const rsvpData = doc.data();
      console.log(`RSVP data for ${rsvpData.email}:`, rsvpData);
      const userProfile = await getUserProfileByEmail(rsvpData.email);
      console.log(`User profile for ${rsvpData.email}:`, userProfile);
      return {
        id: doc.id,
        ...rsvpData,
        // Prioritize RSVP name over user profile name, fallback to Anonymous for legacy accounts
        name: rsvpData.name || userProfile?.name || 'Anonymous',
        displayAnonymous: rsvpData.displayAnonymous || false,
        photoURL: userProfile?.photoURL || null,
        user: userProfile,
        isGuest: userProfile?.isGuest || false,
        // Default to 'yes' for legacy RSVPs without a status
        status: rsvpData.status || 'yes'
      };
    }));

    // Remove duplicates
    const uniqueRSVPs = await removeDuplicateRSVPs(eventId, rsvps);

    console.log(`Processed RSVPs:`, uniqueRSVPs);
    return uniqueRSVPs;
  } catch (error) {
    console.error(`Error fetching RSVPs for event ${eventId}:`, error);
    throw error;
  }
};

// Utility function to remove duplicate RSVPs
const removeDuplicateRSVPs = async (eventId, rsvps) => {
  const uniqueRSVPs = rsvps.reduce((acc, current) => {
    const x = acc.find(item => item.email === current.email);
    if (!x) {
      return acc.concat([current]);
    } else {
      // If duplicate found, keep the one with the earlier createdAt
      if (new Date(current.createdAt) < new Date(x.createdAt)) {
        return acc.map(item => item.email === current.email ? current : item);
      } else {
        return acc;
      }
    }
  }, []);

  // Delete duplicate RSVPs from Firestore
  const duplicates = rsvps.filter(rsvp => 
    !uniqueRSVPs.some(uniqueRsvp => uniqueRsvp.id === rsvp.id)
  );

  for (const duplicate of duplicates) {
    await deleteDoc(doc(db, 'rsvps', duplicate.id));
  }

  // Update event's rsvpCount - include guest counts
  const totalCount = uniqueRSVPs.reduce((total, rsvp) => {
    return total + 1 + (rsvp.guestCount || 0); // 1 for the RSVP person + their guests
  }, 0);
  
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, { rsvpCount: totalCount });

  return uniqueRSVPs;
};

// Handle RSVP for both logged-in and non-logged-in users
export const updateRSVPGuestCount = async (eventId, email, newGuestCount) => {
  try {
    const rsvpsRef = collection(db, 'rsvps');
    const q = query(rsvpsRef, where('eventId', '==', eventId), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { success: false, message: 'RSVP not found' };
    }

    const rsvpDoc = querySnapshot.docs[0];
    const rsvpRef = rsvpDoc.ref;
    const eventRef = doc(db, 'events', eventId);

    await runTransaction(db, async (transaction) => {
      // Get all documents first
      const rsvpSnapshot = await transaction.get(rsvpRef);
      const eventSnapshot = await transaction.get(eventRef);

      if (!eventSnapshot.exists()) {
        throw new Error('Event not found');
      }

      // Calculate the difference in guest count
      const oldGuestCount = rsvpSnapshot.data().guestCount || 0;
      const guestCountDiff = newGuestCount - oldGuestCount;
      const currentCount = eventSnapshot.data().rsvpCount || 0;

      // Then do all updates
      transaction.update(rsvpRef, { guestCount: newGuestCount });
      transaction.update(eventRef, { rsvpCount: currentCount + guestCountDiff });
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating guest count:', error);
    return { success: false, message: error.message };
  }
};

export const handleRSVP = async (email, eventId, isSignedIn, guestCount = 0, name = '', displayAnonymous = false, status = 'yes', comment = '') => {
  // Fetch user and event in parallel for better performance
  const [userDoc, eventDoc] = await Promise.all([
    getUserProfileByEmail(email),
    getDoc(doc(db, 'events', eventId))
  ]);
  const eventData = eventDoc.data();
  
  // CRITICAL: If user exists (ANY type - guest or full) but is NOT signed in,
  // they must login first before RSVPing. This ensures RSVPs are tied to their account.
  // Only allow immediate RSVP if the email has NO existing user document.
  if (userDoc && !isSignedIn) {
    return {
      success: false,
      requiresLogin: true,
      message: 'Please sign in to RSVP with your existing account.',
      error: 'LOGIN_REQUIRED',
      hasGoogleAuth: userDoc.hasGoogleAuth || false,
    };
  }
  
  if (userDoc && isSignedIn) {
    // Only use profile name for RSVP – we don't manage name on RSVP records; they update name in Profile
    const accountName = (userDoc.name && String(userDoc.name).trim()) || '';
    const existingRSVP = await checkRSVP(eventId, email);
    
    if (existingRSVP) {
      // Update existing RSVP with new status and guest count (reuse doc from checkRSVP)
      const oldGuestCount = existingRSVP.guestCount || 0;
      const oldStatus = existingRSVP.status || 'yes';
      
      // Use account name, not the name they typed (for existing users)
      await updateDoc(existingRSVP._doc.ref, {
        status,
        guestCount,
        name: accountName, // Always use account name for existing users
        comment: comment !== undefined ? comment : existingRSVP.comment
      });
      
      // Update event's rsvpCount if guest count changed
      if (oldGuestCount !== guestCount) {
        const eventRef = doc(db, 'events', eventId);
        const eventSnapshot = await getDoc(eventRef);
        const currentCount = eventSnapshot.data().rsvpCount || 0;
        const guestCountDiff = guestCount - oldGuestCount;
        await updateDoc(eventRef, { rsvpCount: currentCount + guestCountDiff });
      }
      
      // Send notification to event host if status or guest count changed (async, non-blocking)
      if ((oldStatus !== status || oldGuestCount !== guestCount) && eventData.hostEmail && eventData.hostEmail !== email) {
        getRSVPs(eventId).then(allRsvps => {
          sendHostRSVPNotification(
            eventData.hostEmail,
            eventData.hostName,
            { ...eventData, url: eventData.url },
            { name: accountName, email, status, guestCount, comment },
            allRsvps,
            true, // isUpdate = true
            oldStatus // Pass the old status
          ).catch(error => {
            console.error('Error sending host notification:', error);
          });
        }).catch(error => {
          console.error('Error fetching RSVPs for notification:', error);
        });
      }

      // Send event details email to attendee when they change their RSVP, or when no change (e.g. they clicked link again – send reminder)
      if (oldStatus !== status || oldGuestCount !== guestCount) {
        sendEventDetailEmail(email, accountName || 'Friend', {
          ...eventData,
          id: eventId,
          url: eventData.url
        }, true, false, status).catch(error => {
          console.error('Error sending RSVP update confirmation email:', error);
        });
      } else {
        // Already RSVP'd with same details – still send confirmation/reminder to attendee and host
        sendEventDetailEmail(email, accountName || 'Friend', {
          ...eventData,
          id: eventId,
          url: eventData.url
        }, true, false, status).catch(error => {
          console.error('Error sending RSVP reminder email:', error);
        });
        if (eventData.hostEmail && eventData.hostEmail !== email) {
          getRSVPs(eventId).then(allRsvps => {
            sendHostRSVPNotification(
              eventData.hostEmail,
              eventData.hostName,
              { ...eventData, url: eventData.url },
              { name: accountName, email, status, guestCount, comment },
              allRsvps
            ).catch(error => {
              console.error('Error sending host reminder notification:', error);
            });
          }).catch(error => {
            console.error('Error fetching RSVPs for reminder notification:', error);
          });
        }
      }

      return { 
        success: true, 
        message: 'Your RSVP has been updated!', 
        isExistingUser: true,
        isRSVPd: true,
        wasAlreadyRSVPd: true,
        rsvpStatus: status,
        guestCount
      };
    } else {
      // Use account name, not the name they typed (for existing users)
      const rsvpName = accountName;
      await addRSVP(eventId, { userId: userDoc.uid, guestCount, name: rsvpName, displayAnonymous, status, comment }, email);
      
      // Send RSVP confirmation email (async, non-blocking)
      sendEventDetailEmail(email, rsvpName || 'Friend', {
        ...eventData,
        id: eventId,
        url: eventData.url
      }, true, false, status).catch(error => {
        console.error('Error sending RSVP confirmation email:', error);
      });
      
      // Send notification to event host (async, non-blocking)
      if (eventData.hostEmail && eventData.hostEmail !== email) {
        getRSVPs(eventId).then(allRsvps => {
          sendHostRSVPNotification(
            eventData.hostEmail,
            eventData.hostName,
            { ...eventData, url: eventData.url },
            { name: accountName, email, status, guestCount, comment },
            allRsvps
          ).catch(error => {
            console.error('Error sending host notification:', error);
          });
        }).catch(error => {
          console.error('Error fetching RSVPs for notification:', error);
        });
      }
      
      return { 
        success: true, 
        message: 'You\'re now RSVP\'d! Check your email for details.', 
        isExistingUser: true,
        isRSVPd: true,
        wasAlreadyRSVPd: false
      };
    }
  } else {
    // New user - RSVP flow, so send welcome email immediately
    const newUser = await createUserDocument(email, name, true);
    createSession(email, false); // Full user, not guest
    await addRSVP(eventId, { userId: newUser.uid, guestCount, name, displayAnonymous, status, comment }, email);
        
    // Send RSVP confirmation email (async, non-blocking)
    sendEventDetailEmail(email, name || 'Friend', {
      ...eventData,
      id: eventId,
      url: eventData.url
    }, true, false, status).catch(error => {
      console.error('Error sending RSVP confirmation email:', error);
    });
    
    // Send notification to event host (async, non-blocking)
    if (eventData.hostEmail && eventData.hostEmail !== email) {
      getRSVPs(eventId).then(allRsvps => {
        sendHostRSVPNotification(
          eventData.hostEmail,
          eventData.hostName,
          { ...eventData, url: eventData.url },
          { name: name || 'Guest', email, status, guestCount, comment },
          allRsvps
        ).catch(error => {
          console.error('Error sending host notification:', error);
        });
      }).catch(error => {
        console.error('Error fetching RSVPs for notification:', error);
      });
    }
    
    return { 
      success: true, 
      message: 'Account created and RSVP confirmed! You\'re now logged in. Check your email for details.', 
      user: newUser, 
      isNewUser: true,
      isRSVPd: true,
      wasAlreadyRSVPd: false
    };
  }
};

// Toggle requiresRSVP flag for an event and delete RSVPs if necessary
export const toggleRequiresRSVP = async (eventId) => {
  const eventRef = doc(db, 'events', eventId);
  
  try {
    const result = await runTransaction(db, async (transaction) => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error("Event does not exist!");
      }
      
      const currentRequiresRSVP = eventDoc.data().requiresRSVP || false;
      
      if (currentRequiresRSVP) {
        // If we're turning off RSVP, delete all RSVPs for this event
        const rsvpsRef = collection(db, 'rsvps');
        const rsvpQuery = query(rsvpsRef, where('eventId', '==', eventId));
        const rsvpSnapshot = await getDocs(rsvpQuery);
        
        rsvpSnapshot.forEach((doc) => {
          transaction.delete(doc.ref);
        });
        
        // Update the event document
        transaction.update(eventRef, { 
          requiresRSVP: false,
          rsvpCount: 0
        });
        
        return { success: true, message: 'RSVP requirement removed and all RSVPs deleted' };
      } else {
        // If we're turning on RSVP, just update the flag
        transaction.update(eventRef, { requiresRSVP: true });
        return { success: true, message: 'RSVP requirement added' };
      }
    });
    
    return result;
  } catch (error) {
    console.error("Error updating RSVP requirement: ", error);
    return { success: false, error: error.message || 'An unknown error occurred' };
  }
};
